import { useMemo } from "react";
import { useVehicles, Vehicle } from "./useVehicles";
import { useTrips, Trip } from "./useTrips";
import { useExpenses, Expense, INITIAL_EXPENSE_TYPES } from "./useExpenses";

export interface VehicleMetrics {
  vehicleId: string;
  vehicleName: string;
  vehicleImage?: string | null;
  totalEarnings: number;
  totalExpenses: number;
  profitabilityPercent: number;
  avgMonthlyProfit: number;
  firstEarningDate: Date | null;
}

export const useVehicleMetrics = () => {
  const { vehicles, isLoading: vehiclesLoading } = useVehicles();
  const { trips, isLoading: tripsLoading } = useTrips();
  const { expenses, isLoading: expensesLoading } = useExpenses();

  const vehicleMetrics = useMemo<VehicleMetrics[]>(() => {
    if (!vehicles || vehicles.length === 0) return [];

    return vehicles.map((vehicle: Vehicle) => {
      // Get trips for this vehicle
      const vehicleTrips = trips?.filter((trip: Trip) => trip.vehicle_id === vehicle.id) || [];
      
      // Get expenses for this vehicle
      const vehicleExpenses = expenses?.filter((expense: Expense) => expense.vehicle_id === vehicle.id) || [];
      
      const totalEarnings = vehicleTrips.reduce((sum, trip) => sum + (trip.total_earnings || trip.revenue || 0), 0);
      const totalExpenses = vehicleExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
      
      // Calculate profitability percent (earnings as % of expenses)
      // If no expenses, show 100% if there are earnings, 0% otherwise
      const profitabilityPercent = totalExpenses > 0 
        ? Math.min(Math.round((totalEarnings / totalExpenses) * 100), 100)
        : (totalEarnings > 0 ? 100 : 0);

      // Find the first earning date for this vehicle
      const tripDates = vehicleTrips
        .filter(trip => trip.trip_start)
        .map(trip => new Date(trip.trip_start!));
      const firstEarningDate = tripDates.length > 0 
        ? new Date(Math.min(...tripDates.map(d => d.getTime())))
        : null;

      // Calculate average monthly profit (excluding "Car Purchase" expenses)
      let avgMonthlyProfit = 0;
      if (firstEarningDate) {
        const now = new Date();
        const monthsDiff = (now.getFullYear() - firstEarningDate.getFullYear()) * 12 
          + (now.getMonth() - firstEarningDate.getMonth()) + 1;
        
        if (monthsDiff > 0) {
          const expensesExcludingInitial = vehicleExpenses
            .filter((expense: Expense) => !INITIAL_EXPENSE_TYPES.includes(expense.expense_type || ""))
            .reduce((sum, expense) => sum + (expense.amount || 0), 0);
          const totalProfit = totalEarnings - expensesExcludingInitial;
          avgMonthlyProfit = totalProfit / monthsDiff;
        }
      }

      return {
        vehicleId: vehicle.id,
        vehicleName: vehicle.name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
        vehicleImage: vehicle.image_url,
        totalEarnings,
        totalExpenses,
        profitabilityPercent,
        avgMonthlyProfit,
        firstEarningDate,
      };
    });
  }, [vehicles, trips, expenses]);

  return {
    vehicleMetrics,
    isLoading: vehiclesLoading || tripsLoading || expensesLoading,
    hasVehicles: (vehicles?.length || 0) > 0,
  };
};
